import { createContext, useContext } from 'react';
import type { PulseState } from './useSequentialPulse';

/**
 * TenantViewContext — provides isTenantView flag and auth credentials to all node components.
 * When isTenantView is true, node components enforce read-only governance based on data.isCustomizable.
 * authToken and tenantApiKey allow nodes to make authenticated API calls without localStorage secrets.
 * Master Admin builder does NOT wrap in this context (defaults to false, no tokens).
 * 
 * Phase 36.5 — Added full sequential pulse state for n8n-style execution visualization.
 * Nodes and edges read `pulseState` to determine their current animation state.
 */
export interface TenantViewContextValue {
  isTenantView: boolean;
  authToken?: string;
  tenantApiKey?: string;
  tenant?: any;
  isWhiteLabelEnabled?: boolean;
  customAvatarUrl?: string;
  customAvatarName?: string;

  // ── Phase 36.5: Sequential Pulse State ──
  /** Full pulse state — nodes/edges read this for animation */
  pulseState?: PulseState;
  /** Legacy: flat array of all currently visible active node IDs */
  activeNodeIds?: string[];
  /** Legacy: trigger pulse with a flat array (for manual/test use) */
  triggerPulse?: (nodeIds: string[]) => void;

  /** Open Portal Data tab and focus on specific node */
  openPortalData?: (nodeType?: string, nodeId?: string) => void;

  /** Open Account & Subscription Billing modal directly from node controls */
  openAccountModal?: () => void;

  // ── Executive Canvas View Mode & Inspector ──
  /** True if canvas is in executive compact badge mode, false if full in-place cards */
  isCompactView?: boolean;
  toggleCompactView?: () => void;
  openInspector?: (nodeId: string) => void;
}

const DEFAULT_PULSE_STATE: PulseState = {
  currentNodeId: null,
  currentEdgeId: null,
  visitedNodeIds: [],
  visitedEdgeIds: [],
  isPulsing: false,
};

export const TenantViewContext = createContext<TenantViewContextValue>({
  isTenantView: false,
  activeNodeIds: [],
  pulseState: DEFAULT_PULSE_STATE,
  isCompactView: true,
});

export const useTenantView = () => useContext(TenantViewContext);
